/**
 * Whether the payment-plan approval flag is on, and what that means for the buttons.
 *
 * While the flag is on, approving or rejecting a plan needs `can_approve_payment_plans`.
 * While it is off, the server accepts any session that passes the approval-limit check,
 * so the buttons are shown as they always were. The server is the gate in both states;
 * this only decides what is worth offering.
 */

import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getPaymentPlanApprovalPolicy } from "@/lib/api";

export const APPROVE_PAYMENT_PLANS = "can_approve_payment_plans";

/**
 * True when the Approve and Reject buttons should be offered.
 *
 * @param {{enforce: boolean, hasPermission: (perm: string) => boolean}} args
 * @returns {boolean}
 */
export function canDecidePlan({ enforce, hasPermission }) {
  return !enforce || hasPermission(APPROVE_PAYMENT_PLANS) === true;
}

/**
 * Read the flag. A failed read answers `{enforce: false}`: the screen is cosmetic and
 * the server refuses a decision the caller may not make, so showing the buttons is the
 * safe way to fail here.
 *
 * @returns {Promise<{enforce: boolean}>}
 */
export async function fetchApprovalPolicy() {
  try {
    const policy = await getPaymentPlanApprovalPolicy();
    return { enforce: policy?.enforce === true };
  } catch {
    return { enforce: false };
  }
}

/**
 * The flag for the current screen, fetched once after the API is ready.
 *
 * @returns {{enforce: boolean}}
 */
export function useApprovalPolicy() {
  const { apiReady } = useAuth();
  const [policy, setPolicy] = useState({ enforce: false });

  useEffect(() => {
    if (!apiReady) return undefined;
    let alive = true;
    fetchApprovalPolicy().then((next) => {
      if (alive) setPolicy(next);
    });
    return () => {
      alive = false;
    };
  }, [apiReady]);

  return policy;
}
